/**
 * PayPal subscription purchase and management page.
 */
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";
import {
  cancelPayPalSubscription,
  completePayPalSubscription,
} from "../scripts/api";
import PageShell from "../components/ui/PageShell";
import GlassPanel from "../components/ui/GlassPanel";
import Button from "../components/ui/Button";
import { useLoadingOverlay } from "../context/LoadingOverlayContext";

function addDays(dateValue, days) {
  const date = new Date(dateValue);
  date.setDate(date.getDate() + days);
  return date;
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString();
}

function getDefaultsFromUser(user) {
  const payTier = String(user?.pay_tier || "").trim().toLowerCase();
  const role = String(user?.role || "").trim().toLowerCase();
  const annualSub = Boolean(user?.annual_sub);
  const autoRenew = Boolean(user?.auto_renew);

  const isActiveMember = payTier === "active" && role === "member";

  if (!isActiveMember) {
    return {
      cycle: "month",
      autoRenew: false,
      existingMember: false,
    };
  }

  return {
    cycle: annualSub ? "year" : "month",
    autoRenew,
    existingMember: true,
  };
}

function getPricing(cycle, autoRenew) {
  if (cycle === "month" && !autoRenew) {
    return {
      priceText: "5 USD",
      expirationText: formatDate(addDays(new Date(), 30)),
    };
  }

  if (cycle === "year" && !autoRenew) {
    return {
      priceText: "45 USD",
      expirationText: formatDate(addDays(new Date(), 365)),
    };
  }

  if (cycle === "month" && autoRenew) {
    return {
      priceText: "4 USD / Month",
      expirationText: "",
    };
  }

  return {
    priceText: "39 USD / Year",
    expirationText: "",
  };
}

function getSelectedPlanId(showTrialPlan, cycle, autoRenew) {
  if (showTrialPlan) {
    return process.env.REACT_APP_PAYPAL_PLAN_YEAR_TRIAL_ONCE;
  }

  if (cycle === "month" && !autoRenew) {
    return process.env.REACT_APP_PAYPAL_PLAN_MONTH_ONCE;
  }

  if (cycle === "year" && !autoRenew) {
    return process.env.REACT_APP_PAYPAL_PLAN_YEAR_ONCE;
  }

  if (cycle === "month" && autoRenew) {
    return process.env.REACT_APP_PAYPAL_PLAN_MONTH_RECUR;
  }

  return process.env.REACT_APP_PAYPAL_PLAN_YEAR_RECUR;
}

export default function Subscription({ session, loading, refreshSession }) {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoadingOverlay();
  const user = session?.user || null;
  const trialEligible = !Boolean(user?.used_trial);

  const [searchParams] = useSearchParams();

  const entryMode = searchParams.get("entry") === "trial" ? "trial" : "paid";
  const showTrialPlan = entryMode === "trial" && trialEligible;

  const paypalStatusUpper = String(user?.paypal_status || "").toUpperCase();
  const paypalSubscriptionTerminal = ["CANCELLED", "EXPIRED", "SUSPENDED"].includes(paypalStatusUpper);

  const hasCancelablePayPalSubscription =
    Boolean(user?.paypal_subscription_id) &&
    Boolean(user?.auto_renew) &&
    !paypalSubscriptionTerminal;

  const trialPlanEnv = String(process.env.REACT_APP_PAYPAL_PLAN_YEAR_TRIAL_ONCE ?? "").trim();
  const userPlanId = String(user?.paypal_plan_id ?? "").trim();
  const isPayPalTrialPlan =
    Boolean(user?.paypal_subscription_id) &&
    !paypalSubscriptionTerminal &&
    (String(user?.last_payment_status || "").toLowerCase() === "trial" ||
      String(user?.pay_tier || "").toLowerCase() === "trial" ||
      (trialPlanEnv !== "" && userPlanId === trialPlanEnv));

  const hasCancelablePayPalTrial = isPayPalTrialPlan && !Boolean(user?.auto_renew);

  const defaults = useMemo(() => getDefaultsFromUser(user), [user]);

  const [cycle, setCycle] = useState(defaults.cycle);
  const [autoRenew, setAutoRenew] = useState(defaults.autoRenew);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const pricing = useMemo(() => getPricing(cycle, autoRenew), [cycle, autoRenew]);

  if (loading) return null;
  if (!session?.authenticated || !user) return <Navigate to="/" replace />;

  async function handleCancelPayPalPlan() {
    setSubmitting(true);
    setMessage("");
    showLoading("Canceling PayPal plan…");
    try {
      await cancelPayPalSubscription();
      await refreshSession?.();
      navigate("/membership");
    } catch (error) {
      setMessage(error.message || "Failed to cancel PayPal plan.");
    } finally {
      hideLoading();
      setSubmitting(false);
    }
  }

  const choiceCardClass =
    "group relative flex cursor-pointer items-center gap-4 rounded-xl border px-4 py-3.5 " +
    "border-white/10 bg-white/[0.04] backdrop-blur-sm shadow-sm transition-all duration-200 " +
    "hover:border-white/20 hover:bg-white/[0.07] hover:shadow-md " +
    "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-sky-400/50 has-[:focus-visible]:ring-offset-2 " +
    "has-[:focus-visible]:ring-offset-black/40 " +
    "has-[input:checked]:border-sky-400/55 has-[input:checked]:bg-sky-500/[0.09] " +
    "has-[input:checked]:shadow-[inset_0_0_0_1px_rgba(56,189,248,0.12),0_4px_24px_-8px_rgba(56,189,248,0.25)]";

  const radioIndicatorOuter =
    "relative flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-2 " +
    "border-white/35 bg-black/35 transition-all duration-200 " +
    "group-has-[input:checked]:border-sky-400 group-has-[input:checked]:bg-sky-400/15 " +
    "group-has-[input:checked]:shadow-[0_0_12px_rgba(56,189,248,0.25)]";

  const radioIndicatorDot =
    "h-2.5 w-2.5 scale-0 rounded-full bg-sky-400 opacity-0 shadow-[0_0_10px_rgba(56,189,248,0.85)] " +
    "transition-all duration-200 group-has-[input:checked]:scale-100 group-has-[input:checked]:opacity-100";

  const checkboxIndicator =
    "flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-md border-2 " +
    "border-white/35 bg-black/35 transition-all duration-200 " +
    "group-has-[input:checked]:border-sky-400 group-has-[input:checked]:bg-sky-400/15 " +
    "group-has-[input:checked]:shadow-[0_0_12px_rgba(56,189,248,0.2)]";

  return (
    <PayPalScriptProvider
      options={{
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID,
        vault: true,
        intent: "subscription",
        disableFunding: ["card"],
      }}
    >
      <PageShell>
        <GlassPanel>
          <div className="space-y-6">
            <h1 className="text-2xl font-bold text-white">
              {showTrialPlan ? "Start Free Trial" : "Subscription"}
            </h1>

            <hr className="border-white/10" />

            {showTrialPlan ? (
              <div className="space-y-3">
                <h2 className="text-base font-bold text-white">Plan</h2>
                <p className="text-white/90">1 Week free trial then 1 Year Subscription</p>
              </div>
            ) : (
              <div className="space-y-4">
                <h2 className="text-base font-bold text-white">Billing Cycle</h2>
                <fieldset className="space-y-3">
                  <legend className="sr-only">Billing cycle</legend>
                  <label className={`group ${choiceCardClass}`}>
                    <input
                      type="radio"
                      name="cycle"
                      value="month"
                      checked={cycle === "month"}
                      onChange={() => setCycle("month")}
                      className="sr-only"
                    />
                    <span className={radioIndicatorOuter} aria-hidden>
                      <span className={radioIndicatorDot} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-white">Month</span>
                      <span className="mt-0.5 block text-sm text-white/55">
                        Pay each month; choose renewal below.
                      </span>
                    </span>
                  </label>
                  <label className={`group ${choiceCardClass}`}>
                    <input
                      type="radio"
                      name="cycle"
                      value="year"
                      checked={cycle === "year"}
                      onChange={() => setCycle("year")}
                      className="sr-only"
                    />
                    <span className={radioIndicatorOuter} aria-hidden>
                      <span className={radioIndicatorDot} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-medium text-white">Year</span>
                      <span className="mt-0.5 block text-sm text-white/55">
                        Best value for longer access.
                      </span>
                    </span>
                  </label>
                </fieldset>

                <label className={`group ${choiceCardClass}`}>
                  <input
                    type="checkbox"
                    checked={autoRenew}
                    onChange={(e) => setAutoRenew(e.target.checked)}
                    className="sr-only"
                  />
                  <span className={checkboxIndicator} aria-hidden>
                    <svg
                      className="h-3.5 w-3.5 text-sky-300 opacity-0 transition-opacity duration-200 group-has-[input:checked]:opacity-100"
                      viewBox="0 0 14 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2.5 7.5L5.5 10.5L11.5 3.5"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-white">Auto renewal</span>
                    <span className="mt-0.5 block text-sm text-white/55">
                      Recurring billing at the lower member rate when enabled.
                    </span>
                  </span>
                </label>
              </div>
            )}

            <div className="space-y-2">
              <h2 className="text-base font-bold text-white">Price:</h2>
              <p className="text-white/90">
                {showTrialPlan ? "0 USD today" : pricing.priceText}
              </p>
            </div>

            {showTrialPlan ? (
              <div className="space-y-2">
                <h2 className="text-base font-bold text-white">Trial Ends:</h2>
                <p className="text-white/90">{formatDate(addDays(new Date(), 7))}</p>
              </div>
            ) : !autoRenew ? (
              <div className="space-y-2">
                <h2 className="text-base font-bold text-white">Expiration:</h2>
                <p className="text-white/90">{pricing.expirationText}</p>
              </div>
            ) : null}

            {message ? (
              <p className="text-sm text-white/80" role="status">
                {message}
              </p>
            ) : null}

            <div className="space-y-3">
              <PayPalButtons
                forceReRender={[showTrialPlan, cycle, autoRenew]}
                createSubscription={(data, actions) => {
                  const planId = getSelectedPlanId(showTrialPlan, cycle, autoRenew);

                  return actions.subscription.create({
                    plan_id: planId,
                    custom_id: String(user.id),
                  });
                }}
                onApprove={async (data) => {
                  showLoading("Finalizing subscription…");
                  try {
                    await completePayPalSubscription({
                      subscriptionID: data.subscriptionID,
                    });

                    await refreshSession?.();
                    navigate("/membership");
                  } catch (error) {
                    setMessage(error.message || "Failed to finalize PayPal subscription.");
                  } finally {
                    hideLoading();
                  }
                }}
                onError={(err) => {
                  console.error(err);
                  setMessage("PayPal subscription failed.");
                }}
              />

              {hasCancelablePayPalTrial ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelPayPalPlan}
                  disabled={submitting}
                >
                  Cancel Trial Period
                </Button>
              ) : null}

              {hasCancelablePayPalSubscription ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCancelPayPalPlan}
                  disabled={submitting}
                >
                  Cancel Subscription
                </Button>
              ) : null}
            </div>
          </div>
        </GlassPanel>
      </PageShell>
    </PayPalScriptProvider>
  );
}
